import React, { createContext, useContext, useState, useEffect } from 'react';
import { lightTheme, darkTheme, designSystem } from '../styles/designSystem';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Clean Rebrandly-style theme (always light mode)
  const fallbackTheme = {
    colors: {
      primary: { 500: '#3b82f6', 600: '#2563eb' },
      text: { primary: '#111827', secondary: '#374151', tertiary: '#6b7280' },
      background: { primary: '#ffffff', secondary: '#fafbfc', tertiary: '#f8f9fa' },
      border: { primary: '#e5e7eb', secondary: '#d1d5db' },
      success: { 500: '#10b981' },
      warning: { 500: '#f59e0b' },
      error: { 500: '#ef4444' }
    },
    spacing: designSystem?.spacing || {
      1: '0.25rem', 2: '0.5rem', 3: '0.75rem', 4: '1rem', 
      5: '1.25rem', 6: '1.5rem', 8: '2rem', 12: '3rem'
    },
    typography: designSystem?.typography || {
      fontSize: {
        xs: '0.75rem', sm: '0.875rem', base: '1rem', lg: '1.125rem',
        xl: '1.25rem', '2xl': '1.5rem', '3xl': '1.875rem'
      },
      fontWeight: {
        normal: '400', medium: '500', semibold: '600', bold: '700'
      }
    },
    borderRadius: designSystem?.borderRadius || {
      sm: '0.125rem', md: '0.375rem', lg: '0.5rem', xl: '0.75rem'
    },
    boxShadow: designSystem?.boxShadow || {
      sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      base: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
    },
    animation: designSystem?.animation || {
      duration: { 200: '200ms', 300: '300ms' },
      easing: { inOut: 'cubic-bezier(0.4, 0, 0.2, 1)', out: 'cubic-bezier(0, 0, 0.2, 1)' }
    }
  };
  
  const [theme, setTheme] = useState(lightTheme || fallbackTheme);

  // Theme is loaded and ready

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    let shouldUseDark = false;
    
    if (savedTheme) {
      shouldUseDark = savedTheme === 'dark';
    } else {
      shouldUseDark = systemPrefersDark;
    }
    
    setIsDarkMode(shouldUseDark);
    setTheme(shouldUseDark ? (darkTheme || fallbackTheme) : (lightTheme || fallbackTheme));
    
    // Update document class for CSS variables
    updateDocumentTheme(shouldUseDark);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!localStorage.getItem('theme')) {
        setIsDarkMode(e.matches);
        setTheme(e.matches ? (darkTheme || fallbackTheme) : (lightTheme || fallbackTheme));
        updateDocumentTheme(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDocumentTheme = (isDark) => {
    const root = document.documentElement;
    const themeData = isDark ? (darkTheme || fallbackTheme) : (lightTheme || fallbackTheme);
    
    // Update CSS custom properties with safe access
    root.style.setProperty('--color-primary', themeData?.colors?.primary?.[500] || '#3b82f6');
    root.style.setProperty('--color-secondary', themeData?.colors?.secondary?.[500] || '#6b7280');
    root.style.setProperty('--color-success', themeData?.colors?.success?.[500] || '#10b981');
    root.style.setProperty('--color-warning', themeData?.colors?.warning?.[500] || '#f59e0b');
    root.style.setProperty('--color-error', themeData?.colors?.error?.[500] || '#ef4444');
    
    root.style.setProperty('--color-bg-primary', themeData?.colors?.background?.primary || '#ffffff');
    root.style.setProperty('--color-bg-secondary', themeData?.colors?.background?.secondary || '#f9fafb');
    root.style.setProperty('--color-bg-tertiary', themeData?.colors?.background?.tertiary || '#f3f4f6');
    
    root.style.setProperty('--color-text-primary', themeData?.colors?.text?.primary || '#111827');
    root.style.setProperty('--color-text-secondary', themeData?.colors?.text?.secondary || '#374151');
    root.style.setProperty('--color-text-tertiary', themeData?.colors?.text?.tertiary || '#6b7280');
    
    root.style.setProperty('--color-border-primary', themeData?.colors?.border?.primary || '#e5e7eb');
    root.style.setProperty('--color-border-secondary', themeData?.colors?.border?.secondary || '#d1d5db');
    
    // Update document class
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  };

  const toggleTheme = () => {
    const newIsDark = !isDarkMode;
    setIsDarkMode(newIsDark);
    setTheme(newIsDark ? (darkTheme || fallbackTheme) : (lightTheme || fallbackTheme));
    localStorage.setItem('theme', newIsDark ? 'dark' : 'light');
    updateDocumentTheme(newIsDark);
  };

  const setLightTheme = () => {
    setIsDarkMode(false);
    setTheme(lightTheme || fallbackTheme);
    localStorage.setItem('theme', 'light');
    updateDocumentTheme(false);
  };

  const setDarkTheme = () => {
    setIsDarkMode(true);
    setTheme(darkTheme || fallbackTheme);
    localStorage.setItem('theme', 'dark');
    updateDocumentTheme(true);
  };

  const setSystemTheme = () => {
    localStorage.removeItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(systemPrefersDark);
    setTheme(systemPrefersDark ? (darkTheme || fallbackTheme) : (lightTheme || fallbackTheme));
    updateDocumentTheme(systemPrefersDark);
  };

  const value = {
    theme,
    isDarkMode,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    setSystemTheme,
    // Utility functions for components
    getColor: (colorPath) => {
      try {
        const keys = colorPath.split('.');
        return keys.reduce((obj, key) => obj?.[key], theme?.colors) || '#6b7280';
      } catch (e) {
        return '#6b7280';
      }
    },
    getSpacing: (value) => theme?.spacing?.[value] || value,
    getFontSize: (size) => theme?.typography?.fontSize?.[size] || size,
    getShadow: (size) => theme?.boxShadow?.[size] || theme?.boxShadow?.base || '0 1px 3px 0 rgb(0 0 0 / 0.1)'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};